/**
 * <zaks-read-more>
 *
 * Clamps a long block of rich text to a few lines and adds a Read more /
 * Read less toggle. Used for the product description, which was previously
 * cut at 45 words server-side with no way to reach the rest of it.
 *
 * Progressive enhancement: the clamp is a class this script adds, so with no
 * JS (or before it runs) the full text is on the page and nothing is hidden.
 *
 *   <zaks-read-more data-more="Read more" data-less="Read less">
 *     <div data-read-more-body>...</div>
 *   </zaks-read-more>
 */
class ZaksReadMore extends HTMLElement {
  static instances = 0;

  onResize = () => {
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => this.enhance());
  };

  connectedCallback() {
    // Also fires when the quick-add modal moves a copy of the buy box into the
    // document; enhancing twice would leave two toggles behind.
    if (this.toggle) return;

    this.body = this.querySelector('[data-read-more-body]');
    if (!this.body) return;

    // Clamp now, measure later. Waiting for the fonts before clamping means a
    // long description paints at full height and then snaps shorter.
    this.classList.add('is-clamped');

    // Measuring before the webfont swaps in reads the fallback's line height,
    // which is how a description that does overflow gets left un-toggled.
    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(() => this.enhance());
    } else {
      this.enhance();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  enhance() {
    if (!this.isConnected || this.toggle) return;

    this.classList.add('is-clamped');
    if (!this.overflows()) {
      this.classList.remove('is-clamped');
      // A description that fits a wide column can overflow a narrow one, so a
      // page that starts at desktop width still gets a toggle when it needs one.
      window.addEventListener('resize', this.onResize);
      return;
    }

    window.removeEventListener('resize', this.onResize);

    ZaksReadMore.instances += 1;
    this.body.id = this.body.id || `ReadMoreBody-${ZaksReadMore.instances}`;

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    this.toggle.className = 'zaks-read-more__toggle';
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-controls', this.body.id);
    this.toggle.textContent = this.dataset.more || 'Read more';
    this.toggle.addEventListener('click', () => this.onToggle());
    this.appendChild(this.toggle);
  }

  onToggle() {
    const expanded = this.classList.toggle('is-clamped') === false;
    this.toggle.setAttribute('aria-expanded', String(expanded));
    this.toggle.textContent = expanded
      ? this.dataset.less || 'Read less'
      : this.dataset.more || 'Read more';
  }

  overflows() {
    // A rounding pixel or two of scrollHeight is not worth a toggle over.
    return this.body.scrollHeight - this.body.clientHeight > 4;
  }
}

customElements.define('zaks-read-more', ZaksReadMore);
